/**
 * Phase 5Z.36 — Fahrer-Portal HR
 * --------------------------------
 * Driver-self-service: vacation balance, sick reporting,
 * Zeitausgleich requests, status of own requests.
 */
import { useEffect, useState } from "react";
import { fahrerApi } from "@/fahrer/lib/api";
import { toast } from "sonner";
import { Plane, HeartPulse, Clock4, Plus, X as XIcon, Loader2, AlertTriangle, Calendar as CalIcon } from "lucide-react";
import { fmtDate } from "@/lib/format";

const TYPE_LABEL = {
  urlaub: "Urlaub", krankenstand: "Krankenstand", zeitausgleich: "Zeitausgleich",
  sonderurlaub: "Sonderurlaub", unbezahlt_frei: "Unbezahlt frei",
  unentschuldigt: "Unentschuldigt", karenz: "Karenz", feiertag: "Feiertag",
};
const STATUS_LABEL = {
  pending: "Ausstehend", approved: "Genehmigt", rejected: "Abgelehnt",
  cancelled: "Storniert", closed: "Abgeschlossen",
};
const STATUS_COLOR = {
  pending: "bg-amber-500/20 text-amber-200 border-amber-500/40",
  approved: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  rejected: "bg-rose-500/20 text-rose-200 border-rose-500/40",
  cancelled: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40",
  closed: "bg-blue-500/20 text-blue-200 border-blue-500/40",
};
const TYPE_COLOR = {
  urlaub: "border-emerald-500/40 text-emerald-200",
  krankenstand: "border-rose-500/40 text-rose-200",
  zeitausgleich: "border-blue-500/40 text-blue-200",
};

// Local fmtDate replaced by shared `@/lib/format` (Phase I)

export default function FahrerHr() {
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [carry, setCarry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'urlaub' | 'krankenstand' | 'zeitausgleich'

  async function load() {
    setLoading(true);
    try {
      const [s, l, co] = await Promise.all([
        fahrerApi.get("/fahrer/hr/summary"),
        fahrerApi.get("/fahrer/hr/absences"),
        fahrerApi.get("/fahrer/hr/carry-over").catch(() => ({ data: null })),
      ]);
      setSummary(s.data); setList(l.data || []);
      setCarry(co.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Daten konnten nicht geladen werden");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function cancelMine(id) {
    if (!window.confirm("Antrag wirklich stornieren?")) return;
    try {
      await fahrerApi.post(`/fahrer/hr/absences/${id}/cancel`);
      toast.success("Storniert");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  }

  if (loading || !summary) {
    return (
      <div className="p-4 text-sm text-zinc-400 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade…
      </div>
    );
  }

  const v = summary.vacation;
  const ot = summary.overtime;

  return (
    <div className="p-4 space-y-4 pb-24" data-testid="fahrer-hr-page">
      <h1 className="font-display text-2xl text-zinc-50 font-bold">Urlaub &amp; Krankenstand</h1>

      {/* Vacation balance hero card */}
      <div className="rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 p-5 text-white shadow-lg" data-testid="fahrer-hr-vac-card">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-emerald-100 text-sm font-semibold">
              <Plane className="w-4 h-4" /> Mein Urlaubskonto
            </div>
            <div className="text-5xl font-mono font-bold tabular-nums mt-2" data-testid="fahrer-hr-vac-remaining">
              {v.remaining_days.toFixed(1)}
            </div>
            <div className="text-emerald-100 text-xs">Resturlaub (Tage)</div>
          </div>
          <button onClick={() => setModal("urlaub")} data-testid="fahrer-hr-btn-urlaub"
            className="bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1 transition">
            <Plus className="w-3.5 h-3.5" /> Antrag
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-white/20">
          <DriverTile label="Anspruch" value={v.entitlement_days.toFixed(1)} />
          <DriverTile label="Verbraucht" value={v.used_days.toFixed(1)} />
          <DriverTile label="Geplant" value={v.planned_days.toFixed(1)} />
        </div>
        {carry && carry.active_total_days > 0 && (
          <div className="mt-3 pt-3 border-t border-white/20" data-testid="fahrer-hr-carry-over">
            <div className="flex items-center justify-between">
              <div className="text-emerald-100 text-xs font-semibold">
                + {carry.active_total_days.toFixed(1)} Tage Resturlaub aus Vorjahren
              </div>
              {carry.next_expiration && (
                <div className="text-[10px] text-emerald-100/70">
                  Ablauf: {fmtDate(carry.next_expiration)}
                </div>
              )}
            </div>
            {carry.expiring_within_90_days > 0 && (
              <div className="text-[10px] text-amber-100 bg-amber-500/20 px-2 py-1 rounded mt-1.5 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {carry.expiring_within_90_days.toFixed(1)} Tage laufen in 90 Tagen ab
              </div>
            )}
          </div>
        )}
        <div className="text-[10px] text-emerald-100/80 mt-2">
          Zyklus {fmtDate(v.year_start)} – {fmtDate(v.year_end)}
        </div>
      </div>

      {/* Quick action row */}
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => setModal("krankenstand")} data-testid="fahrer-hr-btn-sick"
          className="bg-rose-600/90 hover:bg-rose-500 text-white rounded-xl p-4 flex flex-col items-start gap-1 shadow transition">
          <HeartPulse className="w-5 h-5" />
          <div className="font-semibold text-sm">Krankmeldung</div>
          <div className="text-xs text-rose-100/80">Krankenstand erfassen</div>
        </button>
        <button onClick={() => setModal("zeitausgleich")} data-testid="fahrer-hr-btn-za"
          className="bg-blue-600/90 hover:bg-blue-500 text-white rounded-xl p-4 flex flex-col items-start gap-1 shadow transition">
          <Clock4 className="w-5 h-5" />
          <div className="font-semibold text-sm">Zeitausgleich</div>
          <div className="text-xs text-blue-100/80">
            Saldo: {ot.balance_hours.toFixed(1)} h
          </div>
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <SmallStat label="Krankentage" value={summary.sick_days_this_year} accent="text-rose-300" testId="fahrer-hr-sick-days" />
        <SmallStat label="Überstunden" value={`${ot.balance_hours.toFixed(1)}h`} accent={ot.balance_minutes < 0 ? "text-rose-300" : "text-emerald-300"} testId="fahrer-hr-ot-balance" />
        <SmallStat label="Offene Anträge" value={summary.pending_requests} accent="text-amber-300" testId="fahrer-hr-pending" />
      </div>

      {/* Absences list */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
          <CalIcon className="w-4 h-4 text-zinc-400" />
          <div className="font-display text-sm text-zinc-100 font-semibold">Meine Anträge</div>
          <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-500">{list.length}</span>
        </div>
        {list.length === 0 ? (
          <div className="p-6 text-xs text-zinc-500 italic text-center">Noch keine Anträge.</div>
        ) : (
          <div className="divide-y divide-zinc-800" data-testid="fahrer-hr-list">
            {list.slice(0, 50).map(a => (
              <div key={a.id} data-testid={`fahrer-hr-row-${a.id}`} className="px-4 py-3 flex items-center gap-3">
                <div className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded border ${TYPE_COLOR[a.type] || "border-zinc-700 text-zinc-300"}`}>{TYPE_LABEL[a.type]}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-100 font-mono truncate">
                    {fmtDate(a.start_date)} – {fmtDate(a.end_date)}
                  </div>
                  <div className="text-[10px] text-zinc-500">{a.working_days} Tag{a.working_days === 1 ? "" : "e"}{a.comment ? ` · ${a.comment.slice(0, 40)}` : ""}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLOR[a.status] || ""}`}>{STATUS_LABEL[a.status]}</span>
                {a.status === "pending" && !a.is_payroll_locked && (
                  <button onClick={() => cancelMine(a.id)} data-testid={`fahrer-hr-cancel-${a.id}`}
                    className="p-1.5 text-zinc-500 hover:text-rose-300 hover:bg-rose-500/10 rounded">
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <RequestModal type={modal} onClose={() => setModal(null)}
          onSubmitted={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function DriverTile({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-xl font-mono font-bold tabular-nums">{value}</div>
      <div className="text-[10px] text-white/80 uppercase tracking-wider">{label}</div>
    </div>
  );
}
function SmallStat({ label, value, accent, testId }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-2" data-testid={testId}>
      <div className={`text-base font-mono font-bold tabular-nums ${accent || "text-zinc-100"}`}>{value}</div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function RequestModal({ type, onClose, onSubmitted }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    start_date: today, end_date: today,
    half_day: false, comment: "",
    sick_leave_type: "illness", open_ended: false, doctor_note_received: false,
  });
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (form.start_date > form.end_date && !form.open_ended) {
      toast.error("End-Datum ≥ Start-Datum"); return;
    }
    setBusy(true);
    try {
      const url = type === "urlaub" ? "/fahrer/hr/vacation-request"
        : type === "krankenstand" ? "/fahrer/hr/sick-report"
        : "/fahrer/hr/zeitausgleich-request";
      const body =
        type === "urlaub" ? { start_date: form.start_date, end_date: form.end_date, half_day: form.half_day, comment: form.comment }
        : type === "krankenstand" ? {
            start_date: form.start_date, end_date: form.end_date,
            sick_leave_type: form.sick_leave_type, open_ended: form.open_ended,
            doctor_note_received: form.doctor_note_received, comment: form.comment,
          }
        : { start_date: form.start_date, end_date: form.end_date, comment: form.comment };
      await fahrerApi.post(url, body);
      toast.success("Antrag gestellt");
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Senden");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center z-50" data-testid="fahrer-hr-modal">
      <div className="bg-zinc-900 border-t md:border border-zinc-800 rounded-t-2xl md:rounded-xl max-w-md w-full max-h-[90vh] overflow-auto">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-display text-zinc-50 font-bold text-lg">
            {type === "urlaub" ? "Urlaub beantragen"
              : type === "krankenstand" ? "Krankmeldung"
              : "Zeitausgleich beantragen"}
          </h3>
          <button onClick={onClose} className="p-2 text-zinc-500 hover:text-zinc-200" data-testid="fahrer-hr-modal-close">✕</button>
        </div>
        <div className="p-4 space-y-3">
          {type === "krankenstand" && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.open_ended} onChange={e => setForm({ ...form, open_ended: e.target.checked })}
                data-testid="fahrer-hr-form-openended" className="w-4 h-4 accent-rose-500" />
              <span className="text-sm text-zinc-200">Offenes Ende — bis auf weiteres</span>
            </label>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Von</span>
              <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })}
                data-testid="fahrer-hr-form-start"
                className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100" />
            </label>
            {!form.open_ended && (
              <label className="block">
                <span className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Bis</span>
                <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
                  data-testid="fahrer-hr-form-end"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100" />
              </label>
            )}
          </div>
          {type === "urlaub" && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.half_day} onChange={e => setForm({ ...form, half_day: e.target.checked })}
                data-testid="fahrer-hr-form-halfday" className="w-4 h-4 accent-emerald-500" />
              <span className="text-sm text-zinc-200">Halber Tag</span>
            </label>
          )}
          {type === "krankenstand" && (
            <>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Art</span>
                <select value={form.sick_leave_type} onChange={e => setForm({ ...form, sick_leave_type: e.target.value })}
                  data-testid="fahrer-hr-form-sicktype"
                  className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
                  <option value="illness">Krankheit</option>
                  <option value="work_accident">Arbeitsunfall</option>
                  <option value="accident">Unfall (privat)</option>
                  <option value="care_leave">Pflegeurlaub</option>
                  <option value="other">Sonstiges</option>
                </select>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.doctor_note_received} onChange={e => setForm({ ...form, doctor_note_received: e.target.checked })}
                  data-testid="fahrer-hr-form-doc" className="w-4 h-4 accent-rose-500" />
                <span className="text-sm text-zinc-200">Attest bereits vorhanden</span>
              </label>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 flex items-start gap-2 text-xs text-amber-200">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Ab dem 3. Krankheitstag ist ein ärztliches Attest erforderlich. Bitte umgehend nachreichen.</span>
              </div>
            </>
          )}
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Kommentar (optional)</span>
            <textarea rows={2} value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })}
              data-testid="fahrer-hr-form-comment"
              placeholder={type === "urlaub" ? "z.B. Familienurlaub" : type === "krankenstand" ? "z.B. Grippe" : "z.B. nach Wochenend-Dienst"}
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 resize-none" />
          </label>
        </div>
        <div className="p-4 border-t border-zinc-800 flex gap-2">
          <button onClick={onClose} className="flex-1 px-3 py-2.5 text-sm text-zinc-300 bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
          <button onClick={submit} disabled={busy} data-testid="fahrer-hr-form-submit"
            className="flex-1 px-3 py-2.5 text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded inline-flex items-center justify-center gap-1">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Senden
          </button>
        </div>
      </div>
    </div>
  );
}
